
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ChecklistItem {
  id: string;
  card_id: string;
  checklist_id: string;
  text: string;
  completed: boolean;
  assigned_to?: string;
  due_date?: string;
  priority: 'low' | 'medium' | 'high';
  reminder_sent_dates: string[];
  position: number;
  created_at: string;
  updated_at: string;
  created_by?: string;
  assigned_profile?: {
    id: string;
    full_name?: string;
    username?: string;
  };
}

export const useChecklistItems = (cardId: string) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch checklist items for a card
  const { data: checklistItems, isLoading, error } = useQuery({
    queryKey: ['checklistItems', cardId],
    queryFn: async () => {
      if (!cardId) return [];
      
      const { data, error } = await supabase
        .from('checklist_items')
        .select(`
          *,
          assigned_profile:profiles!checklist_items_assigned_to_fkey(id, full_name, username)
        `)
        .eq('card_id', cardId)
        .order('checklist_id')
        .order('position');
      
      if (error) throw error;
      
      // Transform and type the data properly
      return (data || []).map(item => ({
        ...item,
        priority: (item.priority || 'medium') as 'low' | 'medium' | 'high',
        assigned_profile: item.assigned_profile || undefined
      } as unknown as ChecklistItem));
    },
    enabled: !!cardId,
  });

  // Create checklist item
  const createChecklistItem = useMutation({
    mutationFn: async (itemData: {
      card_id: string;
      checklist_id: string;
      text: string;
      assigned_to?: string;
      due_date?: string;
      priority?: 'low' | 'medium' | 'high';
      position: number;
    }) => {
      const { data, error } = await supabase
        .from('checklist_items')
        .insert([itemData])
        .select(`
          *,
          assigned_profile:profiles!checklist_items_assigned_to_fkey(id, full_name, username)
        `)
        .single();
      
      if (error) throw error;
      
      // Transform and type the returned data
      return {
        ...data,
        priority: (data.priority || 'medium') as 'low' | 'medium' | 'high',
        assigned_profile: data.assigned_profile || undefined
      } as unknown as ChecklistItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklistItems', cardId] });
      toast({
        title: "Success",
        description: "Checklist item created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error creating checklist item:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create checklist item",
        variant: "destructive",
      });
    },
  });

  // Update checklist item
  const updateChecklistItem = useMutation({
    mutationFn: async ({ itemId, updates }: { 
      itemId: string; 
      updates: Partial<ChecklistItem> 
    }) => {
      const { data, error } = await supabase
        .from('checklist_items')
        .update(updates)
        .eq('id', itemId)
        .select(`
          *,
          assigned_profile:profiles!checklist_items_assigned_to_fkey(id, full_name, username)
        `)
        .single();
      
      if (error) throw error;
      
      // Transform and type the returned data
      return {
        ...data,
        priority: (data.priority || 'medium') as 'low' | 'medium' | 'high',
        assigned_profile: data.assigned_profile || undefined
      } as unknown as ChecklistItem;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklistItems', cardId] });
    },
    onError: (error: any) => {
      console.error('Error updating checklist item:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update checklist item",
        variant: "destructive",
      });
    },
  });

  // Delete checklist item
  const deleteChecklistItem = useMutation({
    mutationFn: async (itemId: string) => {
      const { error } = await supabase
        .from('checklist_items')
        .delete()
        .eq('id', itemId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklistItems', cardId] });
      toast({
        title: "Success",
        description: "Checklist item deleted successfully",
      });
    },
    onError: (error: any) => {
      console.error('Error deleting checklist item:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete checklist item",
        variant: "destructive",
      });
    },
  });

  return {
    checklistItems: checklistItems || [],
    isLoading,
    error,
    createChecklistItem: createChecklistItem.mutate,
    updateChecklistItem: updateChecklistItem.mutate,
    deleteChecklistItem: deleteChecklistItem.mutate,
    isCreating: createChecklistItem.isPending,
    isUpdating: updateChecklistItem.isPending,
    isDeleting: deleteChecklistItem.isPending,
  };
};
